<script setup>
/**
 * Who a permission may pick — and, far more importantly, the four ways that question can
 * fail to have an answer.
 *
 * THE OWNER NEVER READS THE WORD IN THIS FILE'S NAME. It is the sidecar's word for the
 * thing (`allowlist`), kept here so the mapping stays findable from either side; every
 * string this renders comes from `WASLO.PERSONALITY.PERMISSIONS.CHOICES`, where the vocabulary
 * lint can see it.
 *
 * FIVE STATES, and four of them are invisible in a screenshot of a working workspace — which
 * is exactly why the old build got them wrong:
 *
 *   1. NOT NEEDED — `close` and `note` have nothing to pick. This component is not rendered
 *      at all; a heading with an empty box under it reads as a list that failed to load.
 *   2. READY — Chatwoot answered and has things to offer. Ticks, and the count.
 *   3. NOTHING PICKED while the permission is ON. Not a state of this list at all: the CARD
 *      renders it, from the BFF's `inert`, because it is a fact about the permission
 *      ("your assistant still cannot do this") rather than about the chooser.
 *   4. NOTHING TO PICK — Chatwoot answered and has no teams, no people, or no labels. A
 *      sentence, not an empty box, and it names what is missing.
 *   5. UNREADABLE — Chatwoot could not be read. This is the dangerous one. The old UI
 *      rendered an empty tick list here, which is pixel-identical to "you have chosen
 *      nothing", and a Save from that screen posted an empty list and wiped the owner's
 *      real choices. It needed a hidden marker field, a refusal branch and a replay of the
 *      saved rows as hidden inputs to survive it. Here the fix is structural and one line
 *      long: this component tells the card it is UNREADABLE, the card omits `chosen` from
 *      its patch, and the BFF's partial PATCH leaves the stored list untouched. What the
 *      owner sees is what is saved, read-only, plus what went wrong.
 *
 * ONE MORE THING IT REFUSES TO LOSE: a team or a label that is picked but no longer exists
 * in Chatwoot. The tick list is built from what Chatwoot has UNION what is saved, so a
 * deleted team stays visible, stays ticked, and says why it is odd — rather than vanishing
 * from the list and being dropped by the next save nobody connected to it.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** `'assign'` or `'labels'` — the two permissions that have anything to pick. */
  permissionKey: { type: String, required: true },
  /** `'loading' | 'ready' | 'unreadable'`. */
  state: {
    type: String,
    default: 'loading',
    validator: value => ['loading', 'ready', 'unreadable'].includes(value),
  },
  /** `{ teams, agents, labels }` from Chatwoot, or null while it is not `ready`. */
  targets: { type: Object, default: null },
  /** What is picked right now: `{ teams, agents, labels }`. */
  chosen: { type: Object, required: true },
});

const emit = defineEmits(['update:chosen', 'retry']);

const { t } = useWasloI18n();

const key = name => `WASLO.PERSONALITY.PERMISSIONS.CHOICES.${name}`;

const isAssign = computed(() => props.permissionKey === 'assign');

const chosenCount = computed(
  () =>
    props.chosen.teams.length +
    props.chosen.agents.length +
    props.chosen.labels.length
);

/**
 * Every row to draw: what Chatwoot has, plus anything picked that it no longer has.
 *
 * `kind` is part of the identity, not decoration — a team and a person can share an id, so
 * a list keyed on the number alone would tick the wrong row.
 */
const rows = computed(() => {
  if (props.state !== 'ready' || !props.targets) return [];
  if (isAssign.value) {
    const known = [
      ...props.targets.teams.map(team => ({
        kind: 'team',
        id: team.id,
        name: team.name,
        note: t(key('TEAM')),
      })),
      ...props.targets.agents.map(agent => ({
        kind: 'agent',
        id: agent.id,
        name: agent.name,
        note: t(key('PERSON')),
      })),
    ];
    const seen = new Set(known.map(row => `${row.kind}:${row.id}`));
    const orphans = [
      ...props.chosen.teams.map(team => ({ ...team, kind: 'team' })),
      ...props.chosen.agents.map(agent => ({ ...agent, kind: 'agent' })),
    ]
      .filter(row => !seen.has(`${row.kind}:${row.id}`))
      .map(row => ({
        kind: row.kind,
        id: row.id,
        name: row.name,
        note: row.kind === 'team' ? t(key('TEAM')) : t(key('PERSON')),
      }));
    return [...known, ...orphans];
  }
  const known = props.targets.labels.map(label => ({
    kind: 'label',
    id: label,
    name: label,
    note: '',
  }));
  const seen = new Set(known.map(row => row.id));
  return [
    ...known,
    ...props.chosen.labels
      .filter(label => !seen.has(label))
      .map(label => ({ kind: 'label', id: label, name: label, note: '' })),
  ];
});

const isPicked = row => {
  if (row.kind === 'label') return props.chosen.labels.includes(row.id);
  const list = row.kind === 'team' ? props.chosen.teams : props.chosen.agents;
  return list.some(entry => entry.id === row.id);
};

const toggle = (row, next) => {
  if (row.kind === 'label') {
    const without = props.chosen.labels.filter(label => label !== row.id);
    emit('update:chosen', {
      ...props.chosen,
      labels: next ? [...without, row.id] : without,
    });
    return;
  }
  const field = row.kind === 'team' ? 'teams' : 'agents';
  const without = props.chosen[field].filter(entry => entry.id !== row.id);
  emit('update:chosen', {
    ...props.chosen,
    [field]: next ? [...without, { id: row.id, name: row.name }] : without,
  });
};

/** What is saved, as plain text, for the state where nothing can be ticked. */
const keptNames = computed(() =>
  [
    ...props.chosen.teams.map(team => team.name),
    ...props.chosen.agents.map(agent => agent.name),
    ...props.chosen.labels,
  ].join(t('WASLO.COMMON.LIST_SEPARATOR'))
);

const isEmptyOfChoices = computed(
  () => props.state === 'ready' && rows.value.length === 0
);
</script>

<template>
  <div class="flex flex-col gap-3" :data-waslo-choices="permissionKey">
    <div class="flex flex-col gap-1">
      <h4 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ isAssign ? t(key('TEAMS_HEADING')) : t(key('LABELS_HEADING')) }}
      </h4>
      <p class="mb-0 text-sm leading-6 max-w-[58ch] text-n-slate-11">
        {{
          isAssign ? t(key('TEAMS_DESCRIPTION')) : t(key('LABELS_DESCRIPTION'))
        }}
      </p>
    </div>

    <!-- 1 · reading. -->
    <div
      v-if="state === 'loading'"
      class="flex items-center gap-2 text-sm text-n-slate-11"
      data-waslo-choices-state="loading"
    >
      <Spinner class="size-4" />
      {{ t(key('LOADING')) }}
    </div>

    <!--
      2 · unreadable. NOT an empty tick list: that picture is indistinguishable from
      "nothing is picked", and the save built on it wiped real choices. What is saved is
      shown as text, and the card leaves `chosen` out of its patch entirely.
    -->
    <div
      v-else-if="state === 'unreadable'"
      class="flex flex-col items-start gap-3 p-4 outline outline-1 -outline-offset-1 outline-n-amber-8 rounded-xl bg-n-solid-1"
      data-waslo-choices-state="unreadable"
    >
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ t(key('UNREADABLE')) }}
      </p>
      <p
        v-if="chosenCount > 0"
        class="mb-0 text-sm font-medium text-n-slate-12"
        dir="auto"
        data-waslo-choices-kept
      >
        {{ t(key('UNREADABLE_KEPT'), { names: keptNames }) }}
      </p>
      <Button
        :label="t(key('UNREADABLE_RETRY'))"
        variant="faded"
        color="slate"
        size="sm"
        data-waslo-choices-retry
        @click="emit('retry')"
      />
    </div>

    <!-- 3 · nothing to pick. A sentence naming what is missing, never a blank box. -->
    <p
      v-else-if="isEmptyOfChoices"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
      data-waslo-choices-state="none"
    >
      {{ isAssign ? t(key('NONE_ASSIGN')) : t(key('NONE_LABELS')) }}
    </p>

    <!-- 4 · ready. -->
    <div v-else class="flex flex-col gap-2" data-waslo-choices-state="ready">
      <label
        v-for="row in rows"
        :key="`${row.kind}:${row.id}`"
        class="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer bg-n-alpha-1 hover:bg-n-alpha-2"
        :data-waslo-choice="`${row.kind}:${row.id}`"
      >
        <Checkbox
          :model-value="isPicked(row)"
          @update:model-value="value => toggle(row, value)"
        />
        <!-- The account's own team, person or label name — an inline isolate so an Arabic
             name cannot exchange punctuation with the English note beside it. -->
        <span class="min-w-0 text-sm break-words text-n-slate-12">
          <bdi>{{ row.name }}</bdi>
        </span>
        <span v-if="row.note" class="text-xs shrink-0 text-n-slate-10">
          {{ row.note }}
        </span>
      </label>
      <p class="mb-0 text-xs text-n-slate-10" data-waslo-choices-count>
        {{ t(key('CHOSEN'), chosenCount) }}
      </p>
    </div>
  </div>
</template>
